import { Fragment, useMemo, type CSSProperties } from 'react';
import {
  kenoBoardCells,
  ticketColor,
  type KenoBoardInput,
  type KenoCellVM,
} from '@tv-casino/viewmodels/components/kenoBoardModel.js';
import { useKenoReveal as useSharedKenoReveal } from '@tv-casino/viewmodels/lib/useKenoReveal.js';
import styles from './KenoBoard.module.css';

interface KenoBoardProps extends KenoBoardInput {
  size?: 'phone' | 'tv';
  /**
   * The video-keno cabinet when the host asked for it: blue lacquer balls and
   * lounge-card hues. Off, the board is dressed in the table's felt and
   * tickets wear the seat hues chips use on the other tables.
   */
  classicColors?: boolean;
  /**
   * When given, the cells become pick buttons — the phone's ticket writer.
   * Without it the board is a pure picture, as the TV and the draw view want.
   */
  onSpot?: (spot: number) => void;
}

const cx = (...parts: (string | false | null | undefined)[]) => parts.filter(Boolean).join(' ');

/** Colour ring + pips for a multi-ticket cell; gold ring when nothing is colour-coded. */
function cellStyle(cell: KenoCellVM, classicColors: boolean): CSSProperties | undefined {
  if (cell.ticketIndexes.length === 0) return undefined;
  return {
    ['--ticket-color' as string]: ticketColor(cell.ticketIndexes[0]!, classicColors),
  };
}

/**
 * The 80-number keno board, shared by the phone and the TV. Cells light as
 * the draw is revealed, the viewer's own spots wear a ring, and the
 * intersection — the catches — flash brightest of all. Pass `tickets` to
 * colour-code each wager the way a 4-card keno card does, and `draft` for the
 * ticket still being written on top of them.
 */
export function KenoBoard({
  size = 'phone',
  draw,
  picked,
  tickets,
  draft,
  reveal,
  classicColors = false,
  onSpot,
}: KenoBoardProps) {
  const cells = useMemo(
    () => kenoBoardCells({ draw, picked, tickets, draft, reveal }),
    [draw, picked, tickets, draft, reveal],
  );
  return (
    <div
      className={cx(styles.board, size === 'tv' && styles.tv, classicColors && styles.classic)}
      role={onSpot ? 'group' : 'img'}
      aria-label={
        onSpot
          ? 'Keno board: tap spots to pick them'
          : draw
            ? `Keno board: ${Math.min(reveal ?? draw.length, draw.length)} of ${draw.length} numbers drawn`
            : 'Keno board: 80 numbers, waiting for the draw'
      }
    >
      {cells.map((cell, index) => {
        const colored = cell.ticketIndexes.length > 0;
        const className = cx(
          styles.cell,
          onSpot && styles.pickable,
          cell.drawn && styles.drawn,
          cell.picked && (colored ? styles.ticketed : styles.picked),
          cell.caught && styles.caught,
          cell.draft && styles.draft,
        );
        const pips =
          cell.ticketIndexes.length > 1 ? (
            <span className={styles.pips} aria-hidden="true">
              {cell.ticketIndexes.map((ticketIndex, pip) => (
                <span
                  key={`${ticketIndex}-${pip}`}
                  className={styles.pip}
                  style={{ background: ticketColor(ticketIndex, classicColors) }}
                />
              ))}
            </span>
          ) : null;
        const onTickets = cell.ticketIndexes.length;
        const label =
          `spot ${cell.spot}` +
          (cell.draft ? ', picked' : '') +
          (onTickets > 0 ? `, on ${onTickets} ticket${onTickets === 1 ? '' : 's'}` : '');
        return (
          <Fragment key={cell.spot}>
            {index === 40 && <span aria-hidden="true" className={styles.halves} />}
            {onSpot ? (
              <button
                type="button"
                className={className}
                style={cellStyle(cell, classicColors)}
                aria-pressed={cell.draft}
                aria-label={label}
                onClick={() => onSpot(cell.spot)}
              >
                {cell.spot}
                {pips}
              </button>
            ) : (
              <span className={className} style={cellStyle(cell, classicColors)}>
                {cell.spot}
                {pips}
              </span>
            )}
          </Fragment>
        );
      })}
    </div>
  );
}

const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;

/**
 * The shared reveal clock (`@tv-casino/viewmodels/lib/useKenoReveal`) with
 * the browser's reduced-motion setting read in: the web's two keno screens
 * call this, the native shells call the shared hook with the OS setting.
 */
export function useKenoReveal(
  draw: readonly number[] | null,
  settled: boolean,
  endsAt: number | null,
): number {
  return useSharedKenoReveal(draw, settled, endsAt, prefersReducedMotion());
}
